import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Stack, Group, Text, Badge, Button, ActionIcon, Tooltip, Paper, Progress, Divider, Card, TextInput } from '@mantine/core';
import { IconDeviceFloppy, IconEdit, IconTrash, IconCircleCheck, IconCircle, IconPlus, IconClipboardCheck, IconClock, IconAlertCircle } from '@tabler/icons-react';
import { discardSavedControl, saveControl } from '../../redux/actions/controlActions';
import { persistControlName } from '../../redux/slices/risksSlice';
import PolicyEvidenceBadges from '../../components/PolicyEvidenceBadges';

const statusIcons = {
  completed: { icon: IconCircleCheck, color: '#40c057', label: 'Completed' },
  inProgress: { icon: IconClock, color: '#fd7e14', label: 'In Progress' },
  pending: { icon: IconCircle, color: '#adb5bd', label: 'Not Started' },
  needsAttention: { icon: IconAlertCircle, color: '#fa5252', label: 'Needs Attention' }
};

function getActionStatus(evalData) {
  if (!evalData || (!evalData.control_recurrence && !evalData.executed_next && !evalData.self_evaluation)) {
    return 'pending';
  }
  
  if (evalData.control_recurrence === 'Complete' || evalData.executed_next === 'Complete') {
    return 'completed';
  }
  
  if (evalData.control_recurrence === 'In Progress' || evalData.executed_next === 'In Progress') {
    return 'inProgress';
  }
  
  return 'needsAttention';
}

export default function RiskMitigationsInlineV2({ modelId, assessmentUuid, risk, onEvaluate }) {
  const dispatch = useDispatch();
  const savedControls = useSelector((s) => s.controls.savedControls || {});
  const savedControlEvaluations = useSelector((s) => s.controls.savedControlEvaluations || {});

  const actions = (savedControls[risk.riskId] || []).filter((c) => c.modelId === modelId);

  // DEBUG: Log policy evidence for each action
  console.log('[RiskMitigationsInlineV2] Risk:', risk.riskName);
  console.log('[RiskMitigationsInlineV2] Actions count:', actions.length);
  actions.forEach((action, idx) => {
    console.log(`[RiskMitigationsInlineV2] Action ${idx}:`, {
      name: action.name,
      controlId: action.controlId,
      hasPolicyEvidence: !!action.policyEvidence,
      policyEvidenceLength: action.policyEvidence?.length,
      policyEvidenceType: typeof action.policyEvidence,
      policyEvidence: action.policyEvidence
    });
  });

  // Enhanced action analysis
  const actionStats = useMemo(() => {
    const stats = {
      total: actions.length,
      completed: 0,
      inProgress: 0,
      pending: 0,
      needsAttention: 0,
      evaluated: 0
    };

    actions.forEach(action => {
      const evalData = savedControlEvaluations[risk.riskId]?.[action.controlId]?.[0] || {};
      const status = getActionStatus(evalData);
      
      stats[status]++;
      
      if (evalData.control_recurrence || evalData.executed_next || evalData.self_evaluation) {
        stats.evaluated++;
      }
    });

    const progressPercent = stats.total > 0 ? Math.round((stats.completed / stats.total) * 100) : 0;
    
    return { ...stats, progressPercent };
  }, [actions, savedControlEvaluations, risk.riskId]);

  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [showAddAction, setShowAddAction] = useState(false);
  const [newActionName, setNewActionName] = useState('');

  const startEdit = (a) => {
    setEditingId(a.controlId);
    setEditText(a.name || '');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditText('');
  };

  const confirmEdit = async (a) => {
    const newName = editText.trim() || a.name;

    try {
      // Use async thunk for database persistence
      await dispatch(persistControlName({
        modelId,
        assessmentUuid,
        controlId: a.controlId,
        controlName: newName,
        riskIds: a.riskIds || [risk.riskId]
      })).unwrap();

      cancelEdit();
    } catch (error) {
      console.error('[RiskMitigationsInlineV2] Error persisting control:', error);
      // Keep edit mode open on error so user can try again
    }
  };

  const handleDiscard = (a) => dispatch(discardSavedControl(a, risk.riskId));

  const handleAddAction = async () => {
    if (!newActionName.trim()) return;
    
    await dispatch(saveControl({
      risk,
      control: { name: newActionName.trim() },
      modelId,
      assessmentUuid,
      controlModelType: null,
      controlModelStage: null,
    }));
    
    setNewActionName('');
    setShowAddAction(false);
  };

  // No actions state
  if (actions.length === 0) {
    return (
      <Card withBorder radius="sm" p="md" bg="gray.0">
        <Stack gap="sm">
          <Group justify="space-between" align="center">
            <Stack gap={2}>
              <Text size="sm" fw={500} c="dimmed">Mitigation Actions</Text>
              <Text size="xs" c="dimmed">No actions defined yet for this risk</Text>
            </Stack>
            <Button 
              size="xs" 
              variant="light" 
              leftSection={<IconPlus size={14} />}
              onClick={() => setShowAddAction(true)}
            >
              Add Action
            </Button>
          </Group>
          
          {showAddAction && (
            <>
              <Divider />
              <Group gap="sm">
                <TextInput
                  placeholder="Enter action name..."
                  value={newActionName}
                  onChange={(e) => setNewActionName(e.target.value)}
                  style={{ flex: 1 }}
                  size="xs"
                />
                <ActionIcon size="sm" color="green" variant="light" onClick={handleAddAction}>
                  <IconDeviceFloppy size={14} />
                </ActionIcon>
                <ActionIcon size="sm" color="gray" variant="light" onClick={() => setShowAddAction(false)}>
                  <IconTrash size={14} />
                </ActionIcon>
              </Group>
            </>
          )}
        </Stack>
      </Card>
    );
  }

  return (
    <Card withBorder radius="sm" p="md" bg="blue.0">
      <Stack gap="sm">
        {/* Header with Stats */}
        <Group justify="space-between" align="center">
          <Stack gap={2}>
            <Text size="sm" fw={600} c="blue.8">Mitigation Actions</Text>
            <Group gap="xs">
              <Badge variant="light" color="indigo" size="xs">
                {actionStats.total} actions
              </Badge>
              <Badge variant="light" color={actionStats.evaluated > 0 ? 'green' : 'gray'} size="xs">
                {actionStats.evaluated} evaluated
              </Badge>
            </Group>
          </Stack>
          
          <Stack gap={2} align="flex-end">
            <Text size="xs" c="dimmed">{actionStats.progressPercent}% complete</Text>
            <Progress 
              value={actionStats.progressPercent} 
              color={actionStats.progressPercent > 75 ? 'green' : actionStats.progressPercent > 50 ? 'yellow' : 'red'}
              size="xs"
              w={80}
            />
          </Stack>
        </Group>

        {/* Status Summary */}
        {actionStats.total > 0 && (
          <Group gap="xs" wrap>
            {actionStats.completed > 0 && (
              <Badge variant="light" color="green" size="xs" leftSection="✅">
                {actionStats.completed} done
              </Badge>
            )}
            {actionStats.inProgress > 0 && (
              <Badge variant="light" color="yellow" size="xs" leftSection="⏸️">
                {actionStats.inProgress} in progress
              </Badge>
            )}
            {actionStats.pending > 0 && (
              <Badge variant="light" color="gray" size="xs" leftSection="⭕">
                {actionStats.pending} pending
              </Badge>
            )}
            {actionStats.needsAttention > 0 && (
              <Badge variant="light" color="red" size="xs" leftSection="❌">
                {actionStats.needsAttention} need attention
              </Badge>
            )}
          </Group>
        )}

        <Divider />

        {/* Actions List */}
        <Stack gap="xs">
          {actions.map((action) => {
            const evalData = savedControlEvaluations[risk.riskId]?.[action.controlId]?.[0] || {};
            const status = getActionStatus(evalData);
            const StatusIcon = statusIcons[status].icon;
            const isEditing = editingId === action.controlId;

            return (
              <Paper key={action.controlId} withBorder radius="xs" p="sm" bg="white">
                <Stack gap="xs">
                  <Group justify="space-between" align="center">
                    {isEditing ? (
                      <TextInput
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        style={{ flex: 1 }}
                        size="sm"
                        autoFocus
                      />
                    ) : (
                      <Group gap="xs" align="center" style={{ flex: 1, minWidth: 0 }}>
                        <StatusIcon size={16} color={statusIcons[status].color} />
                        <Text size="sm" style={{ wordBreak: 'break-word', flex: 1 }}>
                          {action.name}
                        </Text>
                        <Badge
                          variant="dot"
                          color={statusIcons[status].color}
                          size="xs"
                        >
                          {statusIcons[status].label}
                        </Badge>
                      </Group>
                    )}

                    <Group gap={4}>
                      {isEditing ? (
                        <>
                          <Tooltip label="Save changes">
                            <ActionIcon size="sm" color="green" variant="light" onClick={() => confirmEdit(action)}>
                              <IconDeviceFloppy size={12} />
                            </ActionIcon>
                          </Tooltip>
                          <Tooltip label="Cancel">
                            <ActionIcon size="sm" color="gray" variant="light" onClick={cancelEdit}>
                              <IconTrash size={12} />
                            </ActionIcon>
                          </Tooltip>
                        </>
                      ) : (
                        <>
                          <Tooltip label="Edit action name">
                            <ActionIcon size="sm" variant="subtle" onClick={() => startEdit(action)}>
                              <IconEdit size={12} />
                            </ActionIcon>
                          </Tooltip>
                          <Tooltip label="Remove action">
                            <ActionIcon size="sm" color="red" variant="subtle" onClick={() => handleDiscard(action)}>
                              <IconTrash size={12} />
                            </ActionIcon>
                          </Tooltip>
                          <Tooltip label={status === 'completed' ? 'Re-evaluate action' : 'Evaluate action'}>
                            <ActionIcon
                              size="sm"
                              color={status === 'completed' ? 'blue' : 'indigo'}
                              variant="light"
                              onClick={() => onEvaluate?.()}
                            >
                              <IconClipboardCheck size={12} />
                            </ActionIcon>
                          </Tooltip>
                        </>
                      )}
                    </Group>
                  </Group>

                  {/* Policy Evidence Badges */}
                  {!isEditing && action.policyEvidence && action.policyEvidence.length > 0 && (
                    <div style={{ marginLeft: 24 }}>
                      <PolicyEvidenceBadges
                        policyEvidence={action.policyEvidence}
                        compact={true}
                        expandable={false}
                      />
                    </div>
                  )}
                </Stack>
              </Paper>
            );
          })}
        </Stack>

        {/* Add New Action */}
        <Divider />
        {showAddAction ? (
          <Group gap="sm">
            <TextInput
              placeholder="Enter new action name..."
              value={newActionName}
              onChange={(e) => setNewActionName(e.target.value)}
              style={{ flex: 1 }}
              size="sm"
            />
            <ActionIcon size="sm" color="green" variant="light" onClick={handleAddAction}>
              <IconDeviceFloppy size={14} />
            </ActionIcon>
            <ActionIcon size="sm" color="gray" variant="light" onClick={() => setShowAddAction(false)}>
              <IconTrash size={14} />
            </ActionIcon>
          </Group>
        ) : (
          <Group justify="space-between">
            <Button 
              size="xs" 
              variant="light" 
              leftSection={<IconPlus size={12} />}
              onClick={() => setShowAddAction(true)}
            >
              Add Action
            </Button>
            <Group gap="xs">
              <Button 
                size="xs" 
                variant="subtle" 
                leftSection={<IconClipboardCheck size={12} />}
                onClick={() => onEvaluate?.()}
                disabled={actionStats.total === 0}
              >
                Evaluate All
              </Button>
            </Group>
          </Group>
        )}
      </Stack>
    </Card>
  );
}